// src/pages/Archive/MeterReports.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Pagination,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Visibility,
  Analytics,
  Search,
  Refresh,
  Download,
  WaterDrop,
  LocalFireDepartment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { getData } from '../../services/storageService';

const MeterReports = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  
  // Додаємо функцію для fallback перекладів
  const tt = (key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meterData, setMeterData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    type: 'all',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const loadMeterData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allRecords = await getData('lainsa_records') || [];
      
      // Фільтруємо ТІЛЬКИ чисті дані лічильників
      const meterRecords = allRecords.filter(record => {
        // Виключаємо звіти операторів (shift_report)
        if (record.recordType === 'shift_report') {
          return false;
        }
        
        // Виключаємо записи з даними виробництва
        if (record.tunel || record.rech_tunel || record.lav_ciclos || record.pesoCliente) {
          return false;
        }
        
        // Залишаємо тільки записи з даними лічильників
        if (record.cont && Object.keys(record.cont).length > 0) {
          return true;
        }
        
        // Або записи з типом meter_reading
        if (record.recordType === 'meter_reading') {
          return true;
        }
        
        return false;
      });
      
      console.log('📊 Pure meter records found:', meterRecords.length);
      console.log('🔍 Pure meter records sample:', meterRecords[0]);
      
      setMeterData(meterRecords);
      setFilteredData(meterRecords);
    } catch (err) {
      setError(tt('archive.load_error', 'Error cargando datos de contadores'));
      console.error('Error loading meter data:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadMeterData();
  }, [loadMeterData]);

  useEffect(() => {
    let result = meterData;

    // Filter by date range
    if (filters.dateFrom) {
      result = result.filter(record => new Date(record.fecha || record.date) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      result = result.filter(record => new Date(record.fecha || record.date) <= new Date(filters.dateTo));
    }

    // Filter by type
    if (filters.type !== 'all') {
      result = result.filter(record => {
        const meterType = getMeterType(record);
        return meterType === filters.type;
      });
    }

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(record =>
        JSON.stringify(record).toLowerCase().includes(searchLower) ||
        (record.id && record.id.toLowerCase().includes(searchLower))
      );
    }

    setFilteredData(result);
    setCurrentPage(1);
  }, [meterData, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getMeterType = (record) => {
    if (record.cont) {
      const cont = record.cont;
      
      // Перевіряємо всі можливі ключі лічильників
      const waterKeys = [
        'agua_tunel', 'agua_pozo', 'agua_lavadoras', 'agua_calderas',
        'agua_tunel_final', 'agua_pozo_final', 'agua_lavadoras_final', 'agua_calderas_final',
        'agua'
      ];
      
      const propaneKeys = [
        'propano_deposito', 'propano_caldera', 'propano_saba', 'propano_medio', 
        'propano_hibri', 'propano_secadora', 'propano_deposito_final', 'propano_caldera_final',
        'propano'
      ];
      
      const hasWater = waterKeys.some(key => 
        cont[key] !== undefined && cont[key] !== null && cont[key] !== '' && cont[key] !== 0
      );
      
      const hasPropane = propaneKeys.some(key => 
        cont[key] !== undefined && cont[key] !== null && cont[key] !== '' && cont[key] !== 0
      );
      
      if (hasWater) return 'water';
      if (hasPropane) return 'propane';
    }
    return 'other';
  };

  const getMeterTypeLabel = (type) => {
    const labels = {
      water: 'Agua',
      propane: 'Propano',
      other: 'Otro'
    };
    return labels[type] || type;
  };

  const getMeterTypeColor = (type) => {
    const colors = {
      water: 'info',
      propane: 'warning',
      other: 'default'
    };
    return colors[type] || 'default';
  };

  const getMeterIcon = (type) => {
    return type === 'water' ? <WaterDrop /> : <LocalFireDepartment />;
  };

  const handleViewDetails = () => {
    navigate('/meter-analysis');
  };

  const handleViewRecord = (recordId) => {
    navigate(`/view/${encodeURIComponent(recordId)}`);
  };

  const handleExportData = () => {
    // TODO: Implement export
    alert(`Exporting ${filteredData.length} meter records...`);
  };

  // Функція для відображення значень лічильників
  const renderMeterValues = (record) => {
    if (!record.cont) return 'Sin datos';
    
    const values = [];
    for (const [key, value] of Object.entries(record.cont)) {
      if (value !== null && value !== undefined && value !== '' && value !== 0) {
        values.push(`${key}: ${value}`);
      }
    }
    
    return values.length > 0 ? values.slice(0, 3).join(', ') + (values.length > 3 ? '...' : '') : 'Sin valores';
  };

  // Функція для відображення списку лічильників
  const renderMeterList = (record) => {
    if (!record.cont) return 'Sin contadores';
    
    const meters = Object.keys(record.cont).filter(key => 
      record.cont[key] !== null && record.cont[key] !== undefined && record.cont[key] !== '' && record.cont[key] !== 0
    );
    
    return meters.length > 0 ? meters.slice(0, 3).join(', ') + (meters.length > 3 ? '...' : '') : 'Sin contadores';
  };

  // Функція для форматування дати
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentPageData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Analytics />
        {tt('archive.meter_reports.title', 'Reportes de Contadores')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        {tt('archive.meter_reports.subtitle', 'Historial de consumo de agua y propano - Solo datos de contadores')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {meterData.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registros Puros
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {meterData.filter(r => getMeterType(r) === 'water').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contadores Agua
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {meterData.filter(r => getMeterType(r) === 'propane').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contadores Propano
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {new Set(meterData.map(r => r.fecha || r.date)).size}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Días con Datos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Info Alert */}
      {meterData.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Esta sección muestra solo registros puros de contadores. 
            Los datos de contadores de los reportes de producción se encuentran en la sección "Reportes Operador".
          </Typography>
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Desde fecha"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Hasta fecha"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filters.type}
                  label="Tipo"
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="water">Agua</MenuItem>
                  <MenuItem value="propane">Propano</MenuItem>
                  <MenuItem value="other">Otros</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Buscar contador..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportData}
                disabled={filteredData.length === 0}
              >
                Exportar Datos
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadMeterData}
              >
                Actualizar
              </Button>
            </Box>
            <Button
              variant="text"
              onClick={() => {
                setFilters({
                  dateFrom: '',
                  dateTo: '',
                  type: 'all',
                  search: ''
                });
              }}
            >
              Limpiar Filtros
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Registros de Contadores Puros ({filteredData.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tt('archive.meter_reports.meters_with_data', 'Contadores con datos')}: {filteredData.length}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Meter Data Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Contadores</TableCell>
                  <TableCell>Valores</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageData.map((record) => {
                  const meterType = getMeterType(record);
                  return (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {record.id?.substring(0, 15)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDate(record.fecha || record.date)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getMeterTypeLabel(meterType)}
                          color={getMeterTypeColor(meterType)}
                          size="small"
                          icon={getMeterIcon(meterType)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" title={record.cont ? Object.keys(record.cont).join(', ') : ''}>
                          {renderMeterList(record)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" title={renderMeterValues(record)}>
                          {renderMeterValues(record)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Ver registro completo">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewRecord(record.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Análisis detallado">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={handleViewDetails}
                            >
                              <Analytics />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredData.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" variant="h6">
                No hay registros puros de contadores
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Esta sección muestra solo registros dedicados exclusivamente a contadores.
                Los datos de contadores de los reportes de producción se encuentran en "Reportes Operador".
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/archive/operator')}
                >
                  Ver Reportes Operador
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/meter-analysis')}
                >
                  Análisis de Contadores
                </Button>
              </Stack>
            </Box>
          )}

          {/* Pagination */}
          {filteredData.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MeterReports;


